import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ProviderType,
  UniversalBookingService,
  CreateServiceInput,
} from "@/types/booking";

export function useUniversalServices(
  providerType: ProviderType | undefined,
  providerId: string | undefined
) {
  const [services, setServices] = useState<UniversalBookingService[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchServices = useCallback(async () => {
    if (!providerType || !providerId) {
      setServices([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("universal_booking_services")
        .select("*")
        .eq("provider_type", providerType)
        .eq("provider_id", providerId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching universal services:", error);
        toast.error("Error al cargar servicios");
        return;
      }

      setServices((data || []) as UniversalBookingService[]);
    } catch (err) {
      console.error("Error in fetchServices:", err);
    } finally {
      setIsLoading(false);
    }
  }, [providerType, providerId]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const createService = async (
    input: Omit<CreateServiceInput, "provider_type" | "provider_id">
  ): Promise<UniversalBookingService | null> => {
    if (!providerType || !providerId) {
      toast.error("Información del proveedor no disponible");
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("universal_booking_services")
        .insert({
          provider_type: providerType,
          provider_id: providerId,
          name: input.name,
          description: input.description || null,
          price: input.price,
          duration_minutes: input.duration_minutes,
          location_type: input.location_type || "in_person",
          max_participants: input.max_participants || 1,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating service:", error);
        toast.error("Error al crear servicio");
        return null;
      }

      toast.success("Servicio creado correctamente");
      await fetchServices();
      return data as UniversalBookingService;
    } catch (err) {
      console.error("Error in createService:", err);
      toast.error("Error inesperado");
      return null;
    }
  };

  const updateService = async (
    serviceId: string,
    updates: Partial<UniversalBookingService>
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_services")
        .update(updates)
        .eq("id", serviceId);

      if (error) {
        console.error("Error updating service:", error);
        toast.error("Error al actualizar servicio");
        return false;
      }

      toast.success("Servicio actualizado");
      await fetchServices();
      return true;
    } catch (err) {
      console.error("Error in updateService:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const deleteService = async (serviceId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_services")
        .delete()
        .eq("id", serviceId);

      if (error) {
        console.error("Error deleting service:", error);
        toast.error("Error al eliminar servicio");
        return false;
      }

      toast.success("Servicio eliminado");
      await fetchServices();
      return true;
    } catch (err) {
      console.error("Error in deleteService:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const toggleServiceActive = async (
    serviceId: string,
    isActive: boolean
  ): Promise<boolean> => {
    return updateService(serviceId, { is_active: isActive });
  };

  return {
    services,
    activeServices: services.filter((s) => s.is_active),
    isLoading,
    createService,
    updateService,
    deleteService,
    toggleServiceActive,
    refetch: fetchServices,
  };
}
